import React, { useEffect, useState } from "react";
import { Button } from "../../../../../design-system";
import {
  VoiceOfCustomer,
  VoiceOfCustomerQuote,
  VoiceOfCustomerQuoteTag,
} from "../../../../../types/CommunityMediaConfig";
import {
  researchCommunityAudience,
  saveCommunityMediaConfig,
  saveVoiceOfCustomer,
} from "../../../../../database/community/mediaConfig";
import {
  VOC_TAGS,
  VocBlock,
  VocChipList,
  VocChip,
  VocChipTag,
  VocChipText,
  VocChipRemove,
  VocAddRow,
  VocAddInput,
  VocActionRow,
  VocTagSelect,
  VocNotes,
  VocMeta,
  ResearchPanel,
  ResearchTitle,
  ResearchPersona,
  ResearchToggle,
  ResearchQuoteList,
  ResearchQuoteRow,
  ResearchQuoteCheckbox,
  ResearchQuoteText,
  ResearchQuoteMeta,
  ResearchSourcesNote,
} from "./editorShared";

export type VoiceOfCustomerEditorProps = {
  communityId?: string;
  initialValue?: VoiceOfCustomer;
  onPersist?: (next: VoiceOfCustomer) => void;
};

type ResearchSuggestion = {
  buyerPersona: string;
  quotes: VoiceOfCustomerQuote[];
  rationale?: string;
  sources: string[];
};

export const VoiceOfCustomerEditor: React.FC<VoiceOfCustomerEditorProps> = ({
  communityId,
  initialValue,
  onPersist,
}) => {
  const [quotes, setQuotes] = useState<VoiceOfCustomerQuote[]>(
    initialValue?.quotes || []
  );
  const [notes, setNotes] = useState<string>(initialValue?.notes || "");
  const [draftText, setDraftText] = useState<string>("");
  const [draftTag, setDraftTag] = useState<VoiceOfCustomerQuoteTag>("language");
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [isResearching, setIsResearching] = useState(false);
  const [research, setResearch] = useState<ResearchSuggestion | null>(null);
  const [pickedQuotes, setPickedQuotes] = useState<Set<number>>(new Set());
  const [includePersona, setIncludePersona] = useState<boolean>(true);

  // Resync when community changes
  useEffect(() => {
    setQuotes(initialValue?.quotes || []);
    setNotes(initialValue?.notes || "");
    setStatus("");
    setResearch(null);
    setPickedQuotes(new Set());
  }, [communityId, initialValue?.quotes, initialValue?.notes]);

  const addDraft = () => {
    const lines = draftText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    if (!lines.length) return;
    const additions: VoiceOfCustomerQuote[] = lines.map((text) => ({
      text,
      tag: draftTag,
    }));
    setQuotes((prev) => [...prev, ...additions]);
    setDraftText("");
    setStatus("");
  };

  const removeQuote = (index: number) => {
    setQuotes((prev) => prev.filter((_, i) => i !== index));
    setStatus("");
  };

  const handleSave = async () => {
    if (!communityId) {
      setStatus("Cannot save without a community.");
      return;
    }
    setIsSaving(true);
    setStatus("Saving...");
    try {
      const next: VoiceOfCustomer = {
        quotes,
        notes: notes.trim() || undefined,
        lastResearchAt: initialValue?.lastResearchAt,
      };
      await saveVoiceOfCustomer(communityId, next);
      setStatus("Saved.");
      onPersist?.(next);
    } catch (err) {
      console.error("Error saving voice of customer:", err);
      setStatus("Error saving. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleResearch = async () => {
    if (!communityId) {
      setStatus("Cannot research without a community.");
      return;
    }
    setIsResearching(true);
    setStatus("Searching the web for audience language...");
    setResearch(null);
    setPickedQuotes(new Set());
    try {
      const result = await researchCommunityAudience({
        communityId,
        apply: false,
      });
      setResearch({
        buyerPersona: result.buyerPersona,
        quotes: result.quotes,
        rationale: result.rationale,
        sources: result.sources,
      });
      // Pre-select all suggested quotes; user can deselect ones they don't want.
      setPickedQuotes(new Set(result.quotes.map((_, i) => i)));
      setIncludePersona(Boolean(result.buyerPersona?.trim()));
      setStatus(
        `Found ${result.quotes.length} suggestion${result.quotes.length === 1 ? "" : "s"}. Review below.`
      );
    } catch (err: any) {
      console.error("Error researching audience:", err);
      setStatus(err?.message || "Research failed. Try again.");
    } finally {
      setIsResearching(false);
    }
  };

  const togglePicked = (index: number) =>
    setPickedQuotes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  const applyResearch = async () => {
    if (!communityId || !research) return;
    const picked = research.quotes.filter((_, i) => pickedQuotes.has(i));
    setIsSaving(true);
    setStatus("Applying research...");
    try {
      const nextQuotes = [...quotes, ...picked];
      const next: VoiceOfCustomer = {
        quotes: nextQuotes,
        notes: notes.trim() || undefined,
        lastResearchAt: new Date().toISOString(),
      };
      await saveCommunityMediaConfig(communityId, {
        voiceOfCustomer: next,
        ...(includePersona && research.buyerPersona.trim()
          ? { buyerPersona: research.buyerPersona.trim() }
          : {}),
      });
      setQuotes(nextQuotes);
      onPersist?.(next);
      setResearch(null);
      setPickedQuotes(new Set());
      setStatus(
        `Added ${picked.length} quote${picked.length === 1 ? "" : "s"}${
          includePersona && research.buyerPersona.trim() ? " + buyer persona" : ""
        }.`
      );
    } catch (err: any) {
      console.error("Error applying research:", err);
      setStatus(err?.message || "Apply failed. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <VocBlock>
      <VocChipList>
        {quotes.length === 0 ? (
          <VocMeta>No audience phrases yet. Add some below.</VocMeta>
        ) : (
          quotes.map((q, i) => (
            <VocChip key={`${q.text}-${i}`}>
              {q.tag && <VocChipTag $tag={q.tag}>{q.tag}</VocChipTag>}
              <VocChipText title={`${q.text}${q.source ? ` — ${q.source}` : ""}`}>
                "{q.text}"
              </VocChipText>
              <VocChipRemove
                type="button"
                aria-label="Remove"
                onClick={() => removeQuote(i)}
              >
                ×
              </VocChipRemove>
            </VocChip>
          ))
        )}
      </VocChipList>

      <VocAddRow>
        <VocAddInput
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          placeholder={
            "Paste verbatim audience phrases here. One per line.\n" +
            'Example: "I keep starting and giving up by week 2"'
          }
        />
        <VocActionRow>
          <VocTagSelect
            value={draftTag}
            onChange={(e) =>
              setDraftTag(e.target.value as VoiceOfCustomerQuoteTag)
            }
          >
            {VOC_TAGS.map((t) => (
              <option key={t.id} value={t.id}>
                Tag: {t.label}
              </option>
            ))}
          </VocTagSelect>
          <Button
            variant="secondary"
            size="sm"
            onClick={addDraft}
            disabled={!draftText.trim()}
          >
            + Add phrase{draftText.split("\n").filter((l) => l.trim()).length > 1 ? "s" : ""}
          </Button>
        </VocActionRow>
      </VocAddRow>

      <VocNotes
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Free-form audience notes (themes, jargon, recurring metaphors)..."
      />

      <VocActionRow>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!communityId}
          isLoading={isSaving}
        >
          Save audience voice
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleResearch}
          disabled={!communityId || isResearching || isSaving}
          isLoading={isResearching}
        >
          🔎 Find audience language
        </Button>
        {status && <VocMeta>{status}</VocMeta>}
        {initialValue?.lastResearchAt && (
          <VocMeta>
            Last researched {new Date(initialValue.lastResearchAt).toLocaleDateString()}
          </VocMeta>
        )}
      </VocActionRow>

      {research && (
        <ResearchPanel>
          <ResearchTitle>Research suggestions</ResearchTitle>
          {research.rationale && <VocMeta>{research.rationale}</VocMeta>}

          {research.buyerPersona.trim() && (
            <>
              <ResearchToggle>
                <input
                  type="checkbox"
                  checked={includePersona}
                  onChange={(e) => setIncludePersona(e.target.checked)}
                />
                <span>Replace buyer persona with the synthesized one below</span>
              </ResearchToggle>
              <ResearchPersona>{research.buyerPersona}</ResearchPersona>
            </>
          )}

          {research.quotes.length > 0 && (
            <>
              <VocMeta>
                {pickedQuotes.size} of {research.quotes.length} quotes selected
              </VocMeta>
              <ResearchQuoteList>
                {research.quotes.map((q, i) => {
                  const picked = pickedQuotes.has(i);
                  return (
                    <ResearchQuoteRow key={i} $picked={picked}>
                      <ResearchQuoteCheckbox
                        checked={picked}
                        onChange={() => togglePicked(i)}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <ResearchQuoteText>"{q.text}"</ResearchQuoteText>
                        <ResearchQuoteMeta>
                          {q.tag && <VocChipTag $tag={q.tag}>{q.tag}</VocChipTag>}
                          {q.source && <span>{q.source}</span>}
                        </ResearchQuoteMeta>
                      </div>
                    </ResearchQuoteRow>
                  );
                })}
              </ResearchQuoteList>
            </>
          )}

          {research.sources.length > 0 && (
            <ResearchSourcesNote>
              Sources: {research.sources.slice(0, 4).join(" · ")}
              {research.sources.length > 4 ? ` · +${research.sources.length - 4} more` : ""}
            </ResearchSourcesNote>
          )}

          <VocActionRow>
            <Button
              variant="primary"
              size="sm"
              onClick={applyResearch}
              isLoading={isSaving}
              disabled={
                isSaving ||
                (pickedQuotes.size === 0 && !(includePersona && research.buyerPersona.trim()))
              }
            >
              Apply selected
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setResearch(null);
                setPickedQuotes(new Set());
              }}
              disabled={isSaving}
            >
              Dismiss
            </Button>
          </VocActionRow>
        </ResearchPanel>
      )}
    </VocBlock>
  );
};

export default VoiceOfCustomerEditor;
